import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AtSign, Trophy, Settings as Cog, Check, MessageSquare, SmilePlus, Users } from "lucide-react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";

const TABS = [
  { value: "all",         label: "ALL",          icon: Bell },
  { value: "mention",     label: "MENTIONS",     icon: AtSign },
  { value: "activity",    label: "ACTIVITY",     icon: MessageSquare },
  { value: "team",        label: "TEAM",         icon: Users },
  { value: "achievement", label: "ACHIEVEMENTS", icon: Trophy },
  { value: "system",      label: "SYSTEM",       icon: Cog },
];

const MENTION_TYPES = new Set(["mention", "mention_community", "mention_internal"]);
const ACTIVITY_TYPES = new Set([
  "comment_on_post", "comment_on_my_community_post", "comment_on_my_internal_post",
  "reaction_on_my_community_post", "reaction_on_my_internal_post",
]);
const TEAM_TYPES = new Set([
  "team_new_post",
  "comment_on_my_internal_post",
  "reaction_on_my_internal_post",
  "mention_internal",
]);
const ACHIEVEMENT_TYPES = new Set(["badge_earned", "rank_up", "achievement"]);

function typeLabel(t) {
  switch (t) {
    case "mention":
    case "mention_community": return "Mention · Community";
    case "mention_internal":  return "Mention · Team";
    case "badge_earned":      return "Badge earned";
    case "rank_up":           return "Rank up";
    case "comment_on_post":
    case "comment_on_my_community_post": return "Reply · Community";
    case "comment_on_my_internal_post":  return "Reply · Team";
    case "reaction_on_my_community_post": return "Reaction · Community";
    case "reaction_on_my_internal_post":  return "Reaction · Team";
    case "team_new_post":     return "New team post";
    case "system":            return "System";
    default:                  return t;
  }
}

function iconForType(t, size = 16) {
  if (MENTION_TYPES.has(t)) return <AtSign size={size} />;
  if (t === "team_new_post") return <Users size={size} />;
  if (t === "comment_on_post" || t === "comment_on_my_community_post" || t === "comment_on_my_internal_post") return <MessageSquare size={size} />;
  if (t === "reaction_on_my_community_post" || t === "reaction_on_my_internal_post") return <SmilePlus size={size} />;
  if (ACHIEVEMENT_TYPES.has(t)) return <Trophy size={size} />;
  return <Bell size={size} />;
}

function filterByTab(notifications, tab) {
  if (tab === "all") return notifications;
  if (tab === "mention") return notifications.filter(n => MENTION_TYPES.has(n.type));
  if (tab === "activity") return notifications.filter(n => ACTIVITY_TYPES.has(n.type));
  if (tab === "team") return notifications.filter(n => TEAM_TYPES.has(n.type));
  if (tab === "achievement") return notifications.filter(n => ACHIEVEMENT_TYPES.has(n.type));
  if (tab === "system") {
    return notifications.filter(n =>
      !MENTION_TYPES.has(n.type) && !ACTIVITY_TYPES.has(n.type) &&
      !TEAM_TYPES.has(n.type) && !ACHIEVEMENT_TYPES.has(n.type)
    );
  }
  return notifications;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/notifications/me");
      setNotifications(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) {
      console.error("Notifications failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read", { mark_all: true });
      setNotifications(ns => ns.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { console.error(e); }
  };

  const markOne = async (id) => {
    try {
      await api.post("/notifications/read", { ids: [id] });
      setNotifications(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch (e) { console.error(e); }
  };

  const filtered = filterByTab(notifications, tab);

  return (
    <div className="min-h-screen bg-fc-navy flex" data-surface="light" data-testid="notifications-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-gray-200 bg-white/70 backdrop-blur sticky top-0 z-30">
          <div>
            <p className="fc-overline text-[10px]">INBOX</p>
            <h1 className="text-lg sm:text-xl font-black tracking-tight">Notifications</h1>
          </div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs tracking-[0.18em] uppercase font-bold text-fc-red hover:underline" data-testid="mark-all-read">
              Mark all read ({unread})
            </button>
          )}
        </div>

        <div className="px-6 lg:px-10 py-8 max-w-3xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  className={`px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold border flex items-center gap-2 ${active ? "border-fc-red bg-fc-red text-white" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                  data-testid={`notif-tab-${t.value}`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-gray-500 text-sm">Nothing here.</p>
          ) : (
            <div className="bg-white border border-gray-200 divide-y divide-white/5">
              {filtered.map(n => {
                const deepLink = n.data?.deep_link;
                const inner = (
                  <div className={`p-4 flex items-start gap-3 ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className="w-10 h-10 bg-fc-red/15 text-fc-red flex items-center justify-center flex-shrink-0">
                      {iconForType(n.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">{n.title}</p>
                      <p className="text-[10px] tracking-[0.18em] uppercase text-gray-500 mt-1">{typeLabel(n.type)} · {n.created_at?.slice(0, 10)}</p>
                    </div>
                    {!n.read && (
                      <button onClick={(e) => { e.preventDefault(); markOne(n.id); }} className="text-gray-400 hover:text-gray-900 text-xs" title="Mark read" data-testid={`mark-one-${n.id}`}>
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                );
                return deepLink ? (
                  <Link key={n.id} to={deepLink}>{inner}</Link>
                ) : <div key={n.id}>{inner}</div>;
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
